/**
 * Queue of retail subscription crypto payments pending admin verification.
 * Requires APPROVE_SUBSCRIPTION_CRYPTO_PAYMENTS, MANAGE_SUBSCRIPTIONS, or SUPER_ADMIN (backend).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { adminApiService } from '../../services/adminApiService';
import './AdminComponents.css';

interface PendingRow {
  upgradeRequestId: number;
  userId: number;
  userEmail: string | null;
  username: string | null;
  fromPlanName: string | null;
  toPlanName: string | null;
  amountPaid: number | string | null;
  currency: string | null;
  cryptoBlockchain: string | null;
  cryptoSymbol: string | null;
  cryptoDestinationAddress: string | null;
  cryptoTxHash: string | null;
  cryptoSubmittedAt: string | null;
  paymentMethod: string | null;
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const SubscriptionCryptoPaymentApprovals: React.FC = () => {
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);
  const [noteById, setNoteById] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiService.getPendingSubscriptionCryptoVerifications();
      if (res.success && Array.isArray(res.data)) {
        setRows(res.data as PendingRow[]);
      } else {
        setError(res.error || 'Failed to load pending verifications');
        setRows([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load pending verifications');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submitVerify = async (upgradeRequestId: number, approved: boolean) => {
    setActionId(upgradeRequestId);
    setError(null);
    try {
      const note = (noteById[upgradeRequestId] || '').trim() || undefined;
      const res = await adminApiService.verifyRetailSubscriptionCryptoPayment({
        upgradeRequestId,
        approved,
        note,
      });
      if (!res.success) {
        setError(res.error || (approved ? 'Approve failed' : 'Reject failed'));
        return;
      }
      setNoteById((prev) => {
        const next = { ...prev };
        delete next[upgradeRequestId];
        return next;
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Request failed');
    } finally {
      setActionId(null);
    }
  };

  const cancelPending = async (upgradeRequestId: number) => {
    const ok = window.confirm('Delete/cancel this unfinished retail subscription registration flow?');
    if (!ok) return;
    setActionId(upgradeRequestId);
    setError(null);
    try {
      const note = (noteById[upgradeRequestId] || '').trim() || undefined;
      const res = await adminApiService.cancelRetailSubscriptionCryptoOrPaymentFlow({
        upgradeRequestId,
        note,
      });
      if (!res.success) {
        setError(res.error || 'Delete pending flow failed');
        return;
      }
      setNoteById((prev) => {
        const next = { ...prev };
        delete next[upgradeRequestId];
        return next;
      });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Delete pending flow failed');
    } finally {
      setActionId(null);
    }
  };

  const formatWhen = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-section" style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: 'Nunito, sans-serif', fontSize: 22, color: '#111827' }}>
            Subscription crypto payments
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280', fontFamily: 'Nunito, sans-serif', maxWidth: 640 }}>
            Review open retail subscription payment registrations (awaiting payment, pending crypto verification, etc).
            You can approve/reject submitted crypto proofs or delete unfinished payment flows.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          style={{
            padding: '8px 18px',
            borderRadius: 999,
            border: 'none',
            background: '#086afb',
            color: '#fff',
            fontWeight: 600,
            fontSize: 13,
            cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'Mukta, sans-serif',
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '12px 14px',
            borderRadius: 8,
            background: 'rgba(255, 113, 113, 0.12)',
            border: '1px solid #ff7171',
            color: '#b91c1c',
            fontSize: 14,
            marginBottom: 16,
            fontFamily: 'Nunito, sans-serif',
          }}
        >
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <p style={{ color: '#6b7280', fontFamily: 'Nunito, sans-serif' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 32,
            textAlign: 'center',
            background: '#fafafa',
            fontFamily: 'Nunito, sans-serif',
            color: '#6b7280',
          }}
        >
          No payments awaiting verification.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Nunito, sans-serif' }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>User</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Plan change</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Amount</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Chain / symbol</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Tx hash</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Submitted</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Note</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.upgradeRequestId} style={{ verticalAlign: 'top' }}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{r.userEmail || r.username || `User ${r.userId}`}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>ID {r.userId} · Request #{r.upgradeRequestId}</div>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    {(r.fromPlanName || '—') + ' → ' + (r.toPlanName || '—')}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    {r.amountPaid != null ? String(r.amountPaid) : '—'} {r.currency || ''}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', wordBreak: 'break-word' }}>
                    {(r.cryptoBlockchain || '—') + ' / ' + (r.cryptoSymbol || '—')}
                    {r.cryptoDestinationAddress && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }} title={r.cryptoDestinationAddress}>
                        To: {r.cryptoDestinationAddress.length > 18
                          ? `${r.cryptoDestinationAddress.slice(0, 10)}…${r.cryptoDestinationAddress.slice(-6)}`
                          : r.cryptoDestinationAddress}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', wordBreak: 'break-all', maxWidth: 200, fontSize: 12 }}>
                    {r.cryptoTxHash || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                    {r.status || '—'}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                    {formatWhen(r.cryptoSubmittedAt || r.updatedAt || r.createdAt || null)}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <input
                      type="text"
                      placeholder="Optional note"
                      value={noteById[r.upgradeRequestId] || ''}
                      onChange={(e) =>
                        setNoteById((prev) => ({ ...prev, [r.upgradeRequestId]: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        minWidth: 120,
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        fontSize: 12,
                      }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {r.status === 'PENDING_CRYPTO_VERIFICATION' ? (
                        <>
                          <button
                            type="button"
                            disabled={actionId === r.upgradeRequestId}
                            onClick={() => void submitVerify(r.upgradeRequestId, true)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: 'none',
                              background: '#0dca9d',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: actionId === r.upgradeRequestId ? 'wait' : 'pointer',
                            }}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={actionId === r.upgradeRequestId}
                            onClick={() => void submitVerify(r.upgradeRequestId, false)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 999,
                              border: '1px solid #ff7171',
                              background: '#fff',
                              color: '#b91c1c',
                              fontWeight: 600,
                              fontSize: 12,
                              cursor: actionId === r.upgradeRequestId ? 'wait' : 'pointer',
                            }}
                          >
                            Reject
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        disabled={actionId === r.upgradeRequestId}
                        onClick={() => void cancelPending(r.upgradeRequestId)}
                        style={{
                          padding: '6px 12px',
                          borderRadius: 999,
                          border: '1px solid #f59e0b',
                          background: '#fff7ed',
                          color: '#9a3412',
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: actionId === r.upgradeRequestId ? 'wait' : 'pointer',
                        }}
                      >
                        Delete pending flow
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCryptoPaymentApprovals;
